import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { DimensionCard } from './DimensionCard';
import { DimensionDetailPanel } from './DimensionDetailPanel';
import type { DimensionDisplayData } from './dimensionHelpers';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';

const ParticleFingerprint = lazy(() =>
  import('./ParticleFingerprint').then(m => ({ default: m.ParticleFingerprint }))
);

interface ParticleTarget {
  xPct: number;
  yPct: number;
}

// ─── Full-screen Reveal (first visit) ───

interface ProfileRevealProps {
  dimensions: DimensionDisplayData[];
  summaryText: string;
  highlightDimensions: DimensionDisplayData[];
  onCollapse: (cardFlights: CardFlightData) => void;
  particleTarget: ParticleTarget | null;
  collapsePhase: number;
}

export interface CardFlightData {
  flights: (DOMRect | null)[];
  targetRect: DOMRect;
  summaryRect?: DOMRect;
  titleRect?: DOMRect;
}

export function ProfileRevealScreen({
  dimensions, summaryText, highlightDimensions,
  onCollapse, particleTarget, collapsePhase,
}: ProfileRevealProps) {
  const [revealPhase, setRevealPhase] = useState(0); // 0=drift, 1=converge, 2=cards
  const [visibleDims, setVisibleDims] = useState(0);
  const [selectedDim, setSelectedDim] = useState<DimensionDisplayData | null>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const summaryRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  // Auto-progress phases
  useEffect(() => {
    const t1 = setTimeout(() => setRevealPhase(1), 600);
    const t2 = setTimeout(() => setRevealPhase(2), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Cascade dimension cards
  useEffect(() => {
    if (revealPhase < 2) return;
    const iv = setInterval(() => {
      setVisibleDims(v => {
        if (v >= dimensions.length) { clearInterval(iv); return v; }
        return v + 1;
      });
    }, 200);
    return () => clearInterval(iv);
  }, [revealPhase, dimensions.length]);

  const handleGoDashboard = useCallback(() => {
    // Measure card positions for FLIP
    const flights = cardRefs.current.map(el => el?.getBoundingClientRect() ?? null);
    const summaryRect = summaryRef.current?.getBoundingClientRect();
    const titleRect = titleRef.current?.getBoundingClientRect();
    // targetRect will be set by the parent after dashboard renders
    onCollapse({ flights, targetRect: new DOMRect(), summaryRect, titleRect });
  }, [onCollapse]);

  const isParticleActive = collapsePhase === 0;
  const allRevealed = visibleDims >= dimensions.length;

  // Compute flight styles during collapse
  const getFlightStyle = (_index: number): React.CSSProperties | undefined => {
    if (collapsePhase < 2) return undefined;
    // Flight styles applied via parent's cardFlights — handled in CollapseTransition
    return undefined;
  };

  return (
    <div
      className="fixed inset-0 z-40 overflow-y-auto"
      style={{
        opacity: collapsePhase >= 3 ? 0 : 1,
        transition: collapsePhase >= 3 ? 'opacity 0.3s ease' : 'none',
        background: '#0B0E1A',
      }}
    >
      {/* "Analysing" text */}
      <div
        className="text-center"
        style={{
          marginTop: 140,
          opacity: revealPhase < 1 ? 1 : 0,
          transform: revealPhase < 1 ? 'none' : 'translateY(-20px)',
          transition: 'all 0.5s ease',
          position: revealPhase >= 1 ? 'absolute' : 'relative',
          left: 0, right: 0,
        }}
      >
        <div className="text-[13px] font-semibold uppercase tracking-widest text-indigo-500 mb-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 mr-2 align-middle animate-dashboard-pulse-indigo" />
          Analysing your responses
        </div>
        <div className="text-sm text-content-dim">Mapping your unique learning fingerprint...</div>
      </div>

      {/* Title */}
      <div
        ref={titleRef}
        className="text-center"
        style={{
          marginTop: revealPhase >= 2 ? 100 : 120,
          opacity: revealPhase >= 1 ? 1 : 0,
          transform: (revealPhase >= 1 ? 'translateY(0)' : 'translateY(20px)') +
            (collapsePhase >= 2 ? ' scale(0.6)' : ''),
          transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="text-xs font-semibold uppercase tracking-wider text-gold-500 mb-2 flex items-center justify-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-gold-500 inline-block animate-dashboard-pulse-gold" />
          Your CogniPrint
        </div>
        <h1 className="font-heading font-extrabold m-0 tracking-tight" style={{ fontSize: revealPhase >= 2 ? 30 : 42, transition: 'font-size 0.8s ease' }}>
          <span className="text-white">Learning </span>
          <span className="bg-gradient-to-br from-indigo-500 via-[#A594F9] to-gold-500 bg-clip-text text-transparent">Fingerprint</span>
        </h1>
      </div>

      {/* Particle canvas */}
      <div
        className="flex justify-center mx-auto relative"
        style={{
          width: revealPhase >= 2 ? 200 : 360,
          height: revealPhase >= 2 ? 200 : 360,
          transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          margin: '16px auto',
          opacity: collapsePhase >= 2 ? 0.4 : 1,
          transform: collapsePhase >= 2 ? 'scale(0.3) translateY(100px)' : 'none',
        }}
      >
        <Suspense fallback={null}>
          <ParticleFingerprint
            active={isParticleActive}
            targetPoint={particleTarget}
            size={revealPhase >= 2 ? 200 : 360}
            canvasSize={600}
          />
        </Suspense>
        {revealPhase < 2 && (
          <div className="absolute inset-[12%] border-2 border-indigo-500/15 border-t-indigo-500 rounded-full animate-spin" />
        )}
      </div>

      {/* Dimension cards */}
      {revealPhase >= 2 && (
        <div className="max-w-[900px] mx-auto px-8">
          {/* Progress counter */}
          <div
            className="text-center mb-4"
            style={{ opacity: collapsePhase >= 2 ? 0 : 1, transition: 'opacity 0.4s ease' }}
          >
            <div className="text-sm text-content-dim font-medium">
              {visibleDims} of {dimensions.length} dimensions revealed
            </div>
            <div className="w-[calc(100%-4px)] h-1 rounded-full bg-dark-subtle mx-auto mt-3 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-500 to-gold-500 transition-[width] duration-300 ease-out"
                style={{ width: `${(visibleDims / dimensions.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            {dimensions.map((dim, i) => (
              <DimensionCard
                key={dim.key}
                dim={dim}
                index={i}
                visible={i < visibleDims}
                animate={i < visibleDims && collapsePhase < 2}
                flightStyle={getFlightStyle(i)}
                cardRef={el => { cardRefs.current[i] = el; }}
                onClick={!dim.locked ? setSelectedDim : undefined}
              />
            ))}
          </div>

          {/* Dimension detail slide-over */}
          <DimensionDetailPanel dim={selectedDim} onClose={() => setSelectedDim(null)} />

          {/* Summary */}
          {allRevealed && (
            <div
              ref={summaryRef}
              className="relative overflow-hidden rounded-2xl border border-indigo-500/15 max-w-[700px] mx-auto mb-5"
              style={{
                background: 'linear-gradient(145deg, #13172c, #111528)',
                padding: '28px 32px',
                opacity: collapsePhase >= 2 ? 0 : 1,
                transform: collapsePhase >= 2 ? 'translateY(-40px) scale(0.9)' : 'none',
                transition: 'all 0.6s cubic-bezier(0.65, 0, 0.15, 1)',
                animation: collapsePhase < 2 ? 'dashboard-fadeUp 0.6s ease-out 0.3s both' : 'none',
              }}
            >
              <div className="absolute pointer-events-none" style={{ top: -50, right: -50, width: 180, height: 180, background: 'radial-gradient(circle, rgba(240,165,0,0.05) 0%, transparent 70%)' }} />
              <div className="text-xs font-semibold uppercase tracking-wider text-gold-500 mb-3">Your Learning Identity</div>
              <p className="text-[15px] leading-[1.7] text-content-secondary m-0">{summaryText}</p>
              <div className="flex gap-2 mt-4 flex-wrap">
                {highlightDimensions.map(d => (
                  <div key={d.key} className="flex items-center gap-1.5 rounded-md" style={{ padding: '4px 10px', background: `${d.color}10`, border: `1px solid ${d.color}20` }}>
                    <div className="w-1.5 h-1.5 rounded-full" style={{ background: d.color, boxShadow: `0 0 5px ${d.color}50` }} />
                    <span className="text-[11px] font-semibold" style={{ color: d.color }}>{d.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* "Go to Dashboard" button */}
      {allRevealed && collapsePhase === 0 && (
        <div className="sticky bottom-5 flex justify-center pb-7" style={{ animation: 'dashboard-fadeUp 0.6s ease-out 0.6s both' }}>
          <button
            onClick={handleGoDashboard}
            className="bg-gradient-to-br from-indigo-500 to-[#5A4BD6] text-white border-none rounded-2xl font-bold text-[15px] cursor-pointer font-body flex items-center gap-3 animate-dashboard-pulse-cta"
            style={{ padding: '15px 34px', boxShadow: '0 4px 28px rgba(108,92,231,0.4)' }}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
              <rect x="2" y="2" width="7" height="7" rx="2" fill="#fff" opacity="0.9" />
              <rect x="11" y="2" width="7" height="4" rx="1.5" fill="#fff" opacity="0.6" />
              <rect x="2" y="11" width="7" height="4" rx="1.5" fill="#fff" opacity="0.6" />
              <rect x="11" y="8" width="7" height="7" rx="2" fill="#fff" opacity="0.9" />
            </svg>
            Go to Dashboard
            <span className="text-[17px]">&rarr;</span>
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Expanded Overlay (from dashboard profile card click) ───

interface ProfileExpandedOverlayProps {
  dimensions: DimensionDisplayData[];
  summaryText: string;
  highlightDimensions: DimensionDisplayData[];
  onClose: () => void;
}

export function ProfileExpandedOverlay({
  dimensions, summaryText, highlightDimensions, onClose,
}: ProfileExpandedOverlayProps) {
  const [visibleDims, setVisibleDims] = useState(0);
  const [selectedDim, setSelectedDim] = useState<DimensionDisplayData | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      let count = 0;
      const iv = setInterval(() => {
        count++;
        setVisibleDims(count);
        if (count >= dimensions.length) clearInterval(iv);
      }, 150);
    }, 400);
    return () => clearTimeout(timer);
  }, [dimensions.length]);

  // Lock body scroll while the overlay is mounted so the page underneath
  // (Dashboard) doesn't render a second outer scrollbar alongside the
  // overlay's own overflow-y-auto.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, []);

  const allRevealed = visibleDims >= dimensions.length;

  return (
    <div
      // top-24 leaves room for AppLayout's sticky h-24 (96px) authenticated
      // header to show through above the overlay. z-40 keeps the overlay
      // below the header's z-50 so the header isn't visually clipped.
      // overflow-x-hidden prevents horizontal scrolling caused by any
      // child shadows or absolute decorations bleeding past the viewport.
      className="fixed left-0 right-0 bottom-0 top-24 z-40 overflow-y-auto overflow-x-hidden"
      style={{
        background: '#0B0E1Af2',
        backdropFilter: 'blur(24px)',
        animation: 'dashboard-fadeIn 0.35s ease',
      }}
    >
      <div className="max-w-[1440px] mx-auto px-6 pt-2 pb-20">
        {/* Header row — title on the left, particle orb centred, Back-to-Dashboard
           button on the right. All three vertically aligned on a single row to
           lift the rest of the page up. */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex-shrink-0">
            <div className="text-xs font-semibold uppercase tracking-wider text-gold-500 mb-1.5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-gold-500 animate-dashboard-pulse-gold" />
              Your CogniPrint
            </div>
            <h1 className="font-heading text-[28px] font-extrabold m-0 tracking-tight">
              <span className="text-white">Learning </span>
              <span className="bg-gradient-to-br from-indigo-500 via-[#A594F9] to-gold-500 bg-clip-text text-transparent">Fingerprint</span>
            </h1>
          </div>

          <div className="flex-shrink-0">
            <Suspense fallback={null}>
              <ParticleFingerprint active size={200} canvasSize={600} />
            </Suspense>
          </div>

          <button
            onClick={onClose}
            className="border border-white/7 text-content-muted rounded-lg text-[13px] font-medium cursor-pointer font-body flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.03)', padding: '8px 16px' }}
          >
            &larr; Back to Dashboard
          </button>
        </div>

        {/* Cards grid — 4 across at desktop, scales down on smaller viewports */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          {dimensions.map((dim, i) => (
            <DimensionCard
              key={dim.key}
              dim={dim}
              index={i}
              visible={i < visibleDims}
              animate={i < visibleDims}
              onClick={!dim.locked ? setSelectedDim : undefined}
              bento
            />
          ))}
        </div>

        {/* Dimension detail slide-over */}
        <DimensionDetailPanel dim={selectedDim} onClose={() => setSelectedDim(null)} />

        {/* Summary — same bento treatment, full grid width */}
        {allRevealed && (
          <div
            className={THICK_TILE_CLASSNAME}
            style={{
              ...THICK_TILE_STYLE,
              padding: '28px 32px',
              animation: 'dashboard-fadeUp 0.6s ease-out 0.3s both',
            }}
          >
            <div className="absolute pointer-events-none" style={{ top: -50, right: -50, width: 180, height: 180, background: 'radial-gradient(circle, rgba(240,165,0,0.05) 0%, transparent 70%)' }} />
            <div className="text-xs font-semibold uppercase tracking-wider text-gold-500 mb-3">Your Learning Identity</div>
            <p className="text-[15px] leading-[1.7] text-content-secondary m-0">{summaryText}</p>
            <div className="flex gap-2 mt-4 flex-wrap">
              {highlightDimensions.map(d => (
                <div key={d.key} className="flex items-center gap-1.5 rounded-md" style={{ padding: '4px 10px', background: `${d.color}10`, border: `1px solid ${d.color}20` }}>
                  <div className="w-1.5 h-1.5 rounded-full" style={{ background: d.color }} />
                  <span className="text-[11px] font-semibold" style={{ color: d.color }}>{d.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

